/* eslint-disable indent */
import React, {
	Dispatch,
	SetStateAction,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { getTimeSheetsForSupervisor, removeRecord } from '../../../services/shiftEndReportService'
import { TimeSheet } from '../../../types/Reports/ShiftEnd/TimeSheet'
import { Activity } from '../../../types/Reports/ShiftEnd/Activity'
import { Employee } from '../../../types/Employee/Employee'
import { useIndexedDB } from '../../hooks/useIndexedDB'
import { useConnectionContext } from '../../navigation/ConnectionContext'
import { getAvailableTimesheetDate } from '../../../services/OfflineServices/foremanCacheService'
import { mapTimeSheetsResponse } from '../../../utils/TimesheetUtils'
import { useShiftEndTimeSheets } from './workersummary/useShiftEndTimeSheets'

type ShiftEndContextType = {
	shiftDate: Date
	filteredData: TimeSheet[]
	workerSummary: TimeSheet[]
	directReportNavPath: number[]
	loadData: (supervisorId: number) => Promise<void>
	lastDirectReport: () => number
	appendToNavPath: (newId: Employee) => void
	popFromNavPath: () => void
	removeActivity: (timeSheetId: number, timeRecordId: number, supervisorId: number) => Promise<void>
	clearLocalStorageOnPageChange: () => void
	setShiftDate: (date: Date) => void
	setIncludeAllTasks: Dispatch<React.SetStateAction<boolean>>
	setSearchText: Dispatch<React.SetStateAction<string>>
	setWorkersTimeRecords: Dispatch<SetStateAction<TimeSheet[]>>
	updateTimeSheet: (
		timesheet: TimeSheet,
		activity: Activity,
		startTime: Date,
		endTime: Date | null,
	) => Promise<void>
	updateActivityComment: (
		timesheetId: number,
		timeRecordId: number,
		comment: string,
	) => Promise<void>
	offlineAvailableDate: Date | null
}

export const ShiftEndContext = createContext<ShiftEndContextType | null>(null)

const useShiftEndContext = (): ShiftEndContextType => {
	const shiftEndContextState = useContext(ShiftEndContext)
	if (shiftEndContextState === null) {
		throw new Error(
			'ShiftEndContext not found. Try wrapping a parent component with <ShiftEndContext.Provider>.',
		)
	}
	return shiftEndContextState
}

type ShiftEndProviderProps = {
	children?: React.ReactNode
}

const ShiftEndProvider = ({ children }: ShiftEndProviderProps) => {
	const [shiftDate, setShiftDate] = useState<Date>(new Date())
	const [workerSummary, setWorkersTimeRecords] = useState<TimeSheet[]>([])
	const [searchText, setSearchText] = useState<string>('')
	const [includeAllTasks, setIncludeAllTasks] = useState<boolean>(false)
	const [directReportNavPath, setDirectReportNavPath] = useState<number[]>(() => {
		// Initialize from localStorage or start with an empty array
		const savedLocations = localStorage.getItem('locations')
		return savedLocations ? JSON.parse(savedLocations) : []
	})
	const db = useIndexedDB()
	const { connectionStatus } = useConnectionContext()
	const [offlineAvailableDate, setOfflineAvailableDate] = useState<Date | null>(null)

	const { updateTimeSheet, updateActivityComment, filteredData } = useShiftEndTimeSheets({
		workerSummary,
		setWorkersTimeRecords,
		shiftDate,
		db,
		searchText,
		includeAllTasks,
	})

	const loadData = async (supervisorId: number) => {
		if (supervisorId && shiftDate) {
			const response = await getTimeSheetsForSupervisor(supervisorId, shiftDate, db)
			setWorkersTimeRecords(mapTimeSheetsResponse(response))
		}
	}

	//#region Supervision Navigation

	const lastDirectReport = () => directReportNavPath[directReportNavPath.length - 1]
	const appendToNavPath = (newEmployee: Employee) => {
		const updatedLocations = [...directReportNavPath, newEmployee.id]
		setDirectReportNavPath(updatedLocations)

		// Save the updated list to localStorage
		localStorage.setItem('locations', JSON.stringify(updatedLocations))
	}

	const popFromNavPath = () => {
		if (directReportNavPath.length > 0) {
			// Remove the last location from the list
			const updatedLocations = directReportNavPath.slice(0, -1)
			setDirectReportNavPath(updatedLocations)

			// Save the updated list to localStorage
			localStorage.setItem('locations', JSON.stringify(updatedLocations))
		}
	}

	const clearLocalStorageOnPageChange = () => {
		setDirectReportNavPath([])
		localStorage.removeItem('locations')
	}

	const location = useLocation()
	useEffect(() => {
		if (!location.pathname.startsWith('/reports')) {
			clearLocalStorageOnPageChange()
		}
	}, [location])

	useEffect(() => {
		if (connectionStatus !== 'online' && db) {
			getAvailableTimesheetDate(db).then((date) => {
				if (date && date instanceof Date) {
					setOfflineAvailableDate(date)
					setShiftDate(date)
				}
			})
		}
	}, [connectionStatus, db])

	//#endregion

	const removeActivity = async (
		timeSheetId: number,
		timeRecordId: number,
		supervisorId: number,
	) => {
		await removeRecord(timeSheetId, timeRecordId, db)
		await loadData(supervisorId)
	}

	return (
		<ShiftEndContext.Provider
			value={{
				shiftDate,
				filteredData,
				workerSummary,
				directReportNavPath,
				loadData,
				setShiftDate,
				setIncludeAllTasks,
				setSearchText,
				setWorkersTimeRecords,
				updateTimeSheet,
				updateActivityComment,
				removeActivity,
				appendToNavPath,
				popFromNavPath,
				lastDirectReport,
				clearLocalStorageOnPageChange,
				offlineAvailableDate,
			}}
		>
			{children}
		</ShiftEndContext.Provider>
	)
}

export { useShiftEndContext, ShiftEndProvider }
